// React and related imports
import React, { createContext, useState, useEffect, useCallback } from 'react';
// External libraries
import { Auth, Storage } from 'aws-amplify';

export const GlobalContext = createContext({
  isAuthenticated: null,
  loadingUser: true,
  checkAuth: async () => {},
  globalSearchQuery: {
    keyword: '',
    departments: [],
    roles: 'all',
    permalink: '',
    type: ''
  },
  setGlobalSearchQuery: () => {},
  resultsData: null,
  setResultsData: () => {},
  suggestionResults: [],
  setSuggestionResults: () => {},
  activeSuggestionIndex: -1,
  setActiveSuggestionIndex: () => {},
  showSuggestions: false,
  setShowSuggestions: () => {},
  displayedResultsCount: 0,
  setDisplayedResultsCount: () => {},
  totalResultsCount: 0,
  setTotalResultsCount: () => {},
  nextToken: null,
  setNextToken: () => {},
  hasMore: false,
  setHasMore: () => {},
  loading: false,
  setLoading: () => {},
  hasSearched: false,
  setHasSearched: () => {},
  selectValue: [],
  setSelectValue: () => {},
  filterValue: 'all',
  setFilterValue: () => {},
  queryParams: new URLSearchParams(),
  setQueryParams: () => {},
  inputValue: '',
  setInputValue: () => {},
  isValid: true,
  setIsValid: () => {},
  minCharactersMet: false,
  setMinCharactersMet: () => {},
  isReadyForSearch: false,
  setIsReadyForSearch: () => {},
  ignoreNextEffect: false,
  setIgnoreNextEffect: () => {},
  departmentsConfig: null,
  setDepartmentsConfig: () => {},
  hasFetchedDepartments: false,
  setHasFetchedDepartments: () => {}
});

export const GlobalProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [globalSearchQuery, setGlobalSearchQuery] = useState({
    keyword: '',
    departments: [],
    roles: 'all',
    permalink: '',
    type: ''
  });
  const [resultsData, setResultsData] = useState(null);
  const [suggestionResults, setSuggestionResults] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [displayedResultsCount, setDisplayedResultsCount] = useState(0);
  const [totalResultsCount, setTotalResultsCount] = useState(0);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectValue, setSelectValue] = useState([]);
  const [filterValue, setFilterValue] = useState('all');
  const [queryParams, setQueryParams] = useState(new URLSearchParams());
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [minCharactersMet, setMinCharactersMet] = useState(false);
  const [isReadyForSearch, setIsReadyForSearch] = useState(false);
  const [ignoreNextEffect, setIgnoreNextEffect] = useState(false);
  const [departmentsConfig, setDepartmentsConfig] = useState(null);
  const [hasFetchedDepartments, setHasFetchedDepartments] = useState(false);


  const checkAuth = useCallback(async () => {
    setLoadingUser(true);
    try {
      await Auth.currentSession(); 
      const credentials = await Auth.currentCredentials();
      setIsAuthenticated(credentials.authenticated);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking authentication:', error);
      }
      setIsAuthenticated(false);
      await Auth.signOut();
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated && departmentsConfig === null) {
      Storage.get('data.json', {
        customPrefix: { public: 'department_data/' },
        download: true
      })
        .then(file => file.Body.text())
        .then(text => {
          const parsedData = JSON.parse(text);
          return Array.isArray(parsedData) ? parsedData : [parsedData];
        }
        )
        .then(parsedData => {
          const departments = parsedData.filter(dept => {
            const s = String(dept.department);
            const isYear     = /^\d{4}$/.test(s);
            const isEmeritus = /^Emeritus\b/.test(s);
            return !isYear && !isEmeritus;
          })
          setDepartmentsConfig(departments);
          setHasFetchedDepartments(true);
        }
        )
        .catch(err => {
          setDepartmentsConfig({});
          setHasFetchedDepartments(true);
        });      
    }
  }, [isAuthenticated, departmentsConfig]);

  const contextValue = {
    isAuthenticated,
    loadingUser,
    globalSearchQuery,
    setGlobalSearchQuery,
    checkAuth,
    resultsData,
    setResultsData,
    suggestionResults,
    setSuggestionResults,
    activeSuggestionIndex,
    setActiveSuggestionIndex,
    showSuggestions,
    setShowSuggestions,
    displayedResultsCount,
    setDisplayedResultsCount,
    totalResultsCount,
    setTotalResultsCount,
    nextToken,
    setNextToken,
    hasMore,
    setHasMore,
    loading,
    setLoading,
    hasSearched,
    setHasSearched,
    selectValue,
    setSelectValue,
    filterValue,
    setFilterValue,
    queryParams,
    setQueryParams,
    inputValue,
    setInputValue,
    isValid,
    setIsValid,
    minCharactersMet,
    setMinCharactersMet,
    isReadyForSearch,
    setIsReadyForSearch,
    ignoreNextEffect,
    setIgnoreNextEffect,
    departmentsConfig,
    setDepartmentsConfig,
    hasFetchedDepartments,
    setHasFetchedDepartments
  };

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};